import { useState } from "react";
import {
  Button,
  Card,
  Grid,
  Typography,
  message,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import logoIcon from "../../assets/images/eko-favicon.png";
import OtpInput from "../../components/OtpInput";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const VerifyComponent = (props) => {
  const { handleVerify, handleResend, handleCorrectEmail, submitting, resending, countdown, errorMessage, orgEmail } = props;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = !!screens.md && !screens.lg;

  const [otp, setOtp] = useState("");

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  const onSubmit = () => {
    if (otp.length !== 6) {
      message.warning("Please enter a valid 6-digit OTP");
      return;
    }
    handleVerify(otp);
  };

  const onResend = () => {
    handleResend();
    setOtp("");
  };

  return (
    <div style={styles.container(isMobile)}>
      <Card style={styles.card(isMobile, isTablet)} bordered={false}>
        <div style={styles.cardContent(isMobile, isTablet)}>
          {/* Header Section */}
          <div style={styles.header(isMobile)}>
            <img src={logoIcon} alt="Company Logo" style={styles.logo(isMobile)} />
            <Title level={2} style={styles.title(isMobile)}>
              Verify Your Email
            </Title>
            <Text style={styles.subtitle(isMobile)}>
              We've sent a verification code to <b>{orgEmail || 'your email'}</b>
            </Text>
          {/* Correct Email Section */}
          <div style={styles.correctEmailSection}>
            <Button
              type="link"
              onClick={handleCorrectEmail}
              style={styles.correctEmailButton(isMobile)}
              disabled={submitting}
            >
              Change Email
            </Button>
          </div>
          </div>

          {/* OTP Input Section */}
          <div style={styles.otpSection(isMobile)}>
            <Paragraph style={styles.otpLabel(isMobile)}>
              Enter the 6-digit verification code
            </Paragraph>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              length={6}
            />
          </div>

          {/* Verify Button */}
          <Button
            type="primary"
            onClick={onSubmit}
            loading={submitting}
            disabled={otp.length !== 6}
            block
            size="large"
            style={styles.button(isMobile)}
            icon={<CheckCircleOutlined />}
          >
            Verify Email
          </Button>

          {/* Resend Section */}
          <div style={styles.resendSection(isMobile)}>
            <Text style={styles.resendText(isMobile)}>
              Didn't receive the code?
            </Text>
            {countdown === -1 ? (
              <Text style={styles.errorText(isMobile)}>
                {errorMessage}
              </Text>
            ) : countdown > 0 ? (
              <Text style={styles.countdownText(isMobile)}>
                Resend in <span style={styles.countdownNumber}>{countdown}s</span>
              </Text>
            ) : (
              <Button
                type="link"
                onClick={onResend}
                loading={resending}
                style={styles.resendButton(isMobile)}
                icon={<ReloadOutlined />}
                disabled={submitting}
              >
                Resend Code
              </Button>
            )}
          </div>

          {/* Help Text */}
          <div style={styles.helpSection(isMobile)}>
            <Text style={styles.helpText(isMobile)}>
              Please check your spam folder if you don't see the email in your inbox.
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Styling for the page (same as signup page)
const styles = {
  container: (isMobile) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100dvh",
    padding: isMobile ? "16px 12px" : "20px",
  }),
  card: (isMobile, isTablet) => ({
    width: "100%",
    maxWidth: isMobile ? "100%" : isTablet ? 560 : 500,
    minWidth: 0,
    padding: 0,
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
    borderRadius: "16px",
    backgroundColor: "#fff",
    overflow: "hidden",
  }),
  cardContent: (isMobile, isTablet) => ({
    padding: isMobile ? "24px 16px" : isTablet ? "36px 28px" : "48px 40px",
    textAlign: "center",
  }),
  header: (isMobile) => ({
    textAlign: "center",
    marginBottom: isMobile ? "24px" : "32px",
  }),
  logo: (isMobile) => ({
    display: "block",
    margin: "0 auto 16px auto",
    height: isMobile ? 56 : 80,
    width: "auto",
    maxWidth: "100%",
  }),
  title: (isMobile) => ({
    margin: "0 0 8px 0",
    color: "#1a1a1a",
    fontWeight: "600",
    fontSize: isMobile ? "24px" : "28px",
    lineHeight: isMobile ? "30px" : "36px",
  }),
  subtitle: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "14px" : "16px",
    margin: 0,
    display: "block",
  }),
  otpSection: (isMobile) => ({
    marginBottom: isMobile ? "24px" : "32px",
  }),
  otpLabel: (isMobile) => ({
    color: "#333",
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "600",
    marginBottom: "16px",
  }),
  button: (isMobile) => ({
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    color: "#fff",
    fontWeight: "600",
    fontSize: isMobile ? "15px" : "16px",
    height: isMobile ? "44px" : "48px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
    transition: "all 0.3s ease",
    marginBottom: "24px",
  }),
  resendSection: (isMobile) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    marginBottom: isMobile ? "20px" : "24px",
  }),
  resendText: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "13px" : "14px",
  }),
  resendButton: (isMobile) => ({
    fontWeight: "600",
    fontSize: isMobile ? "13px" : "14px",
    padding: "4px 0",
    height: "auto",
  }),
  countdownText: (isMobile) => ({
    color: "#999",
    fontSize: isMobile ? "13px" : "14px",
  }),
  countdownNumber: {
    color: "#1890ff",
    fontWeight: "600",
  },
  errorText: (isMobile) => ({
    color: "#ff4d4f",
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "600",
  }),
  correctEmailSection: {
    marginTop: "0px",
    textAlign: "center",
  },
  correctEmailButton: (isMobile) => ({
    fontWeight: "600",
    fontSize: isMobile ? "13px" : "14px",
    padding: "4px 0",
    height: "auto",
  }),
  helpSection: (isMobile) => ({
    padding: isMobile ? "12px" : "16px",
    backgroundColor: "#fafafa",
    borderRadius: "12px",
    marginTop: "8px",
  }),
  helpText: (isMobile) => ({
    color: "#999",
    fontSize: isMobile ? "12px" : "13px",
    lineHeight: "1.5",
  }),
};

export default VerifyComponent;
